import { components, Styles } from 'react-select';
import styled, { css } from 'styled-components';
import { CSSProperties } from 'react';
import { ArrowDown16, Pencil16 } from '@honeybook/icons';
import spacing from '../../styles/spacing';
import { Z_INDEX } from '../../styles/variables';
import { visuallyHidden } from '../../styles/mixins';
import { TScope } from '../../tokens/types';
import type {
    TDropdownSize,
    HBDropdownTheme,
    HBDropdownAlignment
} from './HBDropdown.types';
import { INPUT_SIZE_PROPS } from '../HBTextField/variables';
import { newTextStyles } from '../HBText/HBText.styles';
import HBText from '../HBText/HBText';
import { IDropdown } from './HBDropdown';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { vars } from '../../theme';
import { darken, applyOpacity, isValidColor } from '../../utils/color.utils';
import { ICustomThemeInput } from '../HBTextField/HBTextField.types';

export interface Props {
    dropdownTheme: HBDropdownTheme;
    ignoreStylesOf: any;
    customStyles: Partial<Record<keyof Styles, any>>;
    size: TDropdownSize;
    scope?: TScope;
    focusStyles?: CSSProperties;
    error?: boolean;
    outlineColor?: string;
    withIcon?: boolean;
    menuAlign: HBDropdownAlignment;
    hasCustomMenu?: boolean;
    maxMenuHeight?: CSSProperties['maxHeight'];
    customTheme?: ICustomThemeInput;
}

type SelectWrapperStylesProps = Pick<IDropdown, 'minWidth'>;

export const DEFAULT_STYLES = {
    control: {
        borderRadius: 4,
        warningColor: vars.colors.base.danger
    }
};

const sharedSelectWrapperStyles = css<SelectWrapperStylesProps>`
    min-width: ${({ minWidth }) =>
        minWidth ? `${minWidth} !important` : null};

    input {
        margin: 0 !important;
        box-shadow: none !important;
    }
`;

export const StyledClearIndicatorContainer = styled(components.ClearIndicator)`
    i {
        display: flex;
        svg * {
            transition: fill 100ms;
        }
    }
    &:hover i svg * {
        fill: ${vars.colors.text.primary};
    }
`;

export const CreatableSelectWrapper = styled(
    CreatableSelect
)<SelectWrapperStylesProps>`
    ${sharedSelectWrapperStyles}
`;

export const AsyncSelectWrapper = styled(AsyncSelect)<SelectWrapperStylesProps>`
    ${sharedSelectWrapperStyles}
`;

export const AsyncCreatableSelectWrapper = styled(
    AsyncCreatableSelect
)<SelectWrapperStylesProps>`
    ${sharedSelectWrapperStyles}
`;

export function getDropdownBorderRadius(
    borderRadius: number,
    size: TDropdownSize
) {
    const percentBorderRadius = borderRadius / 100;
    return (
        percentBorderRadius *
        parseInt(INPUT_SIZE_PROPS.builder[size].height, 10)
    );
}

export const IconSpan = styled.span`
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 6px;
    i {
        display: flex;
    }
`;

export const OptionWrapper = styled.div<{ isSubLabel?: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    ${({ isSubLabel }) =>
        isSubLabel &&
        css`
            gap: ${spacing[4]};
        `}
`;

export const LabelSpan = styled(HBText).attrs<{
    $hasIcon?: boolean;
    $hasSubLabel?: boolean;
    $fontColor?: string;
}>(props => ({
    ellipsis: true,
    tooltipProps: {
        delayShowInMs: 500,
        targetContainerStyle: {
            flexShrink: 1,
            flexGrow: 0,
            flexBasis: 'auto',
            maxWidth: props.$hasSubLabel ? '85%' : '100%'
        }
    }
}))<{ $hasIcon?: boolean; $hasSubLabel?: boolean; $fontColor?: string }>`
    ${({ $hasIcon, $fontColor }) => css`
        ${$hasIcon &&
        css`
            margin-right: auto;
        `}
        ${$fontColor &&
        css`
            color: ${$fontColor};
        `}
    `}
`;

export const SelectionDescription = styled.span`
    ${visuallyHidden}
`;

export const SubLabelSpan = styled(HBText).attrs({
    ellipsis: true,
    type: 'body12',
    tooltipProps: {
        disabled: false,
        delayShowInMs: 500,
        targetContainerStyle: {
            flexShrink: 1,
            flexGrow: 0,
            flexBasis: 'auto',
            minWidth: 0
        }
    }
})<{
    $isSubLabelGreyed?: boolean;
}>`
    position: relative;
    top: 1px;
    color: ${({ $isSubLabelGreyed }) =>
        $isSubLabelGreyed
            ? vars.colors.text.disabled
            : vars.colors.text.tertiary};
    font-weight: 400;
`;

// react-select style overrides come as an object or as a function of the
// default styles and the component state.
const resolveCustomStyle = (custom: any, provided: any, state: any) =>
    typeof custom === 'function' ? custom(provided, state) : custom;

export const getCustomStyles = ({
    dropdownTheme,
    ignoreStylesOf,
    customStyles = {},
    focusStyles = {},
    size = 'sm',
    scope = 'system',
    error = false,
    outlineColor = vars.colors.stroke.secondary,
    withIcon,
    menuAlign,
    hasCustomMenu = false,
    maxMenuHeight = '300px',
    customTheme
}: Props) => {
    const styles: Styles = {
        container: (provided, state) => {
            return {
                ...provided,
                fontSize: '14px',
                minWidth: '100px',
                width: '100%',
                fontFamily: vars.fonts.body,
                cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                pointerEvents: 'all',
                ...customStyles.container
            };
        },
        control: (provided, state) => {
            const controlStyles = {
                ...provided,
                display: 'flex',
                // react-select wraps the control by default. With the multi
                // value container sizing from its content, that would push the
                // indicators onto a second row.
                ...(state.isMulti && { flexWrap: 'nowrap' }),
                borderRadius: `${DEFAULT_STYLES.control.borderRadius}px`,
                height: INPUT_SIZE_PROPS[scope][size].height,
                minHeight: '36px',
                boxShadow: 'none',
                cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                '&:hover, &:focus-within': {
                    borderColor: error
                        ? DEFAULT_STYLES.control.warningColor
                        : outlineColor
                },
                border: `1px solid ${
                    error ? DEFAULT_STYLES.control.warningColor : 'transparent'
                }`
            };

            const warningColor = DEFAULT_STYLES.control.warningColor;
            const fontColor =
                customTheme?.fontColor || vars.colors.text.primary;
            const bg = customTheme?.background || vars.colors.surface.item;
            const hoverBg =
                customTheme && isValidColor(bg) ? darken(bg, 0.1) : bg;

            const themedBase = customTheme
                ? {
                      ...controlStyles,
                      color: fontColor,
                      borderRadius:
                          customTheme.borderRadius ||
                          `${DEFAULT_STYLES.control.borderRadius}px`
                  }
                : controlStyles;

            const resolveBorderColor = (fallback: string) =>
                customTheme
                    ? customTheme.borderColor || applyOpacity(fontColor, 25)
                    : fallback;

            switch (dropdownTheme) {
                case 'plain': {
                    return {
                        ...themedBase,
                        background: customTheme
                            ? 'transparent'
                            : state.menuIsOpen
                            ? applyOpacity(vars.colors.surface.item, 40)
                            : vars.colors.surface.item,
                        border: `1px solid ${
                            error
                                ? warningColor
                                : resolveBorderColor(
                                      vars.colors.stroke.secondary
                                  )
                        }`,
                        ...(!customTheme && {
                            boxShadow: vars.shadow['10']
                        }),
                        ...customStyles.control
                    };
                }
                case 'underline': {
                    return {
                        ...themedBase,
                        background: customTheme
                            ? 'transparent'
                            : vars.colors.surface.item,
                        borderWidth: 0,
                        borderStyle: 'none',
                        borderBottom: `1px solid ${
                            error
                                ? warningColor
                                : resolveBorderColor(vars.colors.base.neutral)
                        }`,
                        borderRadius: 0,
                        ...customStyles.control
                    };
                }
                case 'transparent': {
                    if (!customTheme) {
                        return {
                            ...themedBase,
                            ...customStyles.control,
                            background: 'transparent',
                            '&:hover': {
                                borderColor: 'transparent'
                            }
                        };
                    }
                    return {
                        ...themedBase,
                        background: 'transparent',
                        border: error ? `1px solid ${warningColor}` : 'none',
                        '&:hover, &:focus-within': {
                            borderColor: error ? warningColor : 'transparent'
                        },
                        ...customStyles.control
                    };
                }
                default: {
                    if (customTheme) {
                        return {
                            ...themedBase,
                            background:
                                state.menuIsOpen || state.isFocused
                                    ? hoverBg
                                    : bg,
                            border: error
                                ? `1px solid ${warningColor}`
                                : `1px solid ${
                                      customTheme.borderColor || 'transparent'
                                  }`,
                            '&:hover': { background: hoverBg },
                            ...customStyles.control
                        };
                    }
                    const result = {
                        ...themedBase,
                        background:
                            state.menuIsOpen || state.isFocused
                                ? vars.colors.surface.itemInsetHover
                                : vars.colors.surface.itemInset,
                        borderWidth: 0,
                        color: state.isDisabled
                            ? vars.colors.text.disabled
                            : vars.colors.text.primary,
                        ...customStyles.control
                    };
                    return state.menuIsOpen
                        ? { ...result, ...focusStyles }
                        : result;
                }
            }
        },
        dropdownIndicator: provided => ({
            ...provided,
            ...customStyles.dropdownIndicator
        }),
        clearIndicator: provided => ({
            ...provided,
            ...customStyles.clearIndicator
        }),
        indicatorSeparator: () => ({
            ...customStyles.indicatorSeparator
        }),
        indicatorsContainer: provided => ({
            ...provided,
            ...customStyles.indicatorsContainer
        }),
        placeholder: provided => ({
            ...provided,
            width: '98%',
            padding: 0,
            margin: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: customTheme?.fontColor || vars.colors.text.tertiary,
            opacity: customTheme?.fontColor ? 0.6 : 1,
            ...customStyles.placeholder
        }),
        menu: provided => ({
            ...provided,
            borderRadius: '4px',
            margin: '4px 0 0 0',
            boxShadow: vars.shadow['40'],
            'z-index': `${Z_INDEX.TOP_MOST}`,
            right: menuAlign === 'end' ? 0 : null,
            minWidth: '100%',
            width: 'max-content',
            maxWidth: '400px',
            ...(customTheme?.background
                ? { background: customTheme.background }
                : {}),
            ...(!hasCustomMenu
                ? {
                      maxHeight: maxMenuHeight,
                      padding: '8px 0',
                      display: 'flex',
                      flexDirection: 'column'
                  }
                : {}),
            ...customStyles.menu
        }),
        menuList: provided => ({
            ...provided,
            padding: '8px 0',
            maxHeight: maxMenuHeight,
            ...(!hasCustomMenu
                ? {
                      padding: 0,
                      maxHeight: 'none',
                      flex: 1
                  }
                : {}),
            ...customStyles.menuList
        }),
        menuPortal: provided => ({
            ...provided,
            zIndex: Z_INDEX.POPOVER + Z_INDEX.TOP_MOST,
            ...customStyles.menuPortal
        }),
        option: (provided, state) => {
            const baseOption = {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: '38px',
                color: state.isDisabled
                    ? provided.color
                    : vars.colors.text.primary,
                userSelect: state.isDisabled ? 'none' : 'all',
                cursor: provided.isDisabled ? 'not-allowed' : 'pointer',
                ':active': {
                    backgroundColor: 'transparent'
                },
                '&:hover': {
                    background: 'transparent'
                },
                '&:first-of-type': {
                    borderTopLeftRadius: '2px',
                    borderTopRightRadius: '2px'
                },
                '&:last-of-type': {
                    borderBottomColor: 'transparent',
                    borderBottomLeftRadius: '2px',
                    borderBottomRightRadius: '2px'
                }
            };
            const customOptionStyles =
                typeof customStyles.option === 'function'
                    ? customStyles.option(provided, state)
                    : customStyles.option;

            if (customTheme) {
                const fontColor =
                    customTheme.fontColor || vars.colors.text.primary;
                const bg = customTheme.background || vars.colors.surface.item;
                const hoverBg = isValidColor(bg) ? darken(bg, 0.1) : bg;
                return {
                    ...baseOption,
                    color: fontColor,
                    '& [data-interactive]': {
                        backgroundColor: `${bg} !important`
                    },
                    '& [data-highlighted] > div': {
                        backgroundColor: `${applyOpacity(
                            fontColor,
                            15
                        )} !important`
                    },
                    '&:hover': { background: hoverBg },
                    '& span': { color: fontColor },
                    ...customOptionStyles
                };
            }

            return {
                ...baseOption,
                ...customOptionStyles
            };
        },
        singleValue: provided => ({
            ...provided,
            color: customTheme?.fontColor || vars.colors.text.primary,
            ...customStyles.singleValue
        }),
        valueContainer: (provided, state) => ({
            ...provided,
            // react-select's `flex: 1` has a zero basis, which hides the chips'
            // width (and this container's padding) from a shrink-to-fit parent.
            ...(state.isMulti && { flexBasis: 'auto' }),
            flexWrap: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            paddingLeft: withIcon ? '8px' : '12px',
            ...customStyles.valueContainer
        }),
        // Chips never shrink: the value container measures their natural width
        // to decide how many fit. `maxWidth` lets a lone long chip truncate.
        multiValue: (provided, state) => ({
            ...provided,
            flexShrink: 0,
            maxWidth: '100%',
            ...resolveCustomStyle(customStyles.multiValue, provided, state)
        }),
        // The remove button supplies a chip's right padding. A chip without
        // one (the "+N" chip) mirrors the left padding instead.
        multiValueLabel: (provided, state) => ({
            ...provided,
            '&:last-child': { paddingRight: provided.paddingLeft },
            ...resolveCustomStyle(customStyles.multiValueLabel, provided, state)
        }),
        input: provided => ({
            ...provided,
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            padding: 0,
            margin: 0,
            input: {
                fontFamily: 'inherit'
            },
            ...customStyles.input
        }),
        groupHeading: provided => ({
            ...provided,
            padding: '12px 12px 8px',
            display: 'flex',
            alignItems: 'end',
            color: vars.colors.text.tertiary,
            ...newTextStyles.subtitle12,
            ...customStyles.groupHeading
        }),
        group: provided => ({
            ...provided,
            '&:not(:last-of-type)': {
                borderBottom: `1px solid ${vars.colors.stroke.secondary}`
            },
            ...customStyles.group
        })
    };

    Object.keys(ignoreStylesOf).forEach(styleType => {
        if (ignoreStylesOf[styleType]) {
            styles[styleType] = () => {};
        }
    });

    return styles;
};

export const ArrowIcon = styled(ArrowDown16)`
    display: flex;
`;

export const PencilIcon = styled(Pencil16)`
    display: flex;
`;

export const CustomIconContainer = styled.div`
    i {
        display: flex;
    }
    padding-left: 12px;
`;
